// Listing package produced by the Apps Setup AI wizard and saved with the app
// skeleton. Importing copies it into the drafts (review) layer only — nothing
// reaches Google without the usual explicit submit. The panel renders nothing
// when no package is linked to this Play app.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getWizardPackage, importWizardPackage } from '@/api/play';

export function WizardPackagePanel({ appId }: { appId: number }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<string>('');
  const pkg = useQuery({
    queryKey: ['play-wizard-package', appId],
    queryFn: () => getWizardPackage(appId),
  });

  const doImport = useMutation({
    mutationFn: () => importWizardPackage(appId),
    onSuccess: (r) => {
      setResult(`Imported ${r.listings} locale(s) and ${r.images} screenshot(s) into drafts.`);
      qc.invalidateQueries({ queryKey: ['play-drafts', appId] });
    },
  });

  if (!pkg.data?.available) return null;

  return (
    <div className="bg-surface border border-primary/30 rounded-lg p-3 flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-content">Apps Setup listing package</p>
        <p className="text-xs text-content-subtle mt-0.5 truncate" title={pkg.data.langs.join(', ')}>
          {result || `${pkg.data.langs.length} locale(s) · ${pkg.data.screenshots} screenshot(s) · ${pkg.data.langs.join(', ')}`}
        </p>
        {doImport.error && <p className="text-xs text-red-500 mt-0.5">{(doImport.error as Error).message}</p>}
      </div>
      <button
        type="button"
        disabled={doImport.isPending}
        onClick={() => {
          if (window.confirm('Import the package into drafts? Existing drafts for these locales will be overwritten.')) {
            setResult('');
            doImport.mutate();
          }
        }}
        className="shrink-0 px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
      >
        {doImport.isPending ? 'Importing…' : 'Import to drafts'}
      </button>
    </div>
  );
}
